<template>
    <v-card class="mx-auto">
        <v-card-title class="bg-primary">
            {{ cardTitle }}
        </v-card-title>
        <div column wrap class="mx-auto">
            <v-container v-if="settingUpUser">
                <v-text-field label="First Name" v-model="firstName" variant="underlined" color="primary"></v-text-field>
                <v-text-field label="Last Name" v-model="lastName" variant="underlined" color="primary"></v-text-field>
                <v-text-field label="New Password" v-model="newPassword" type="password" variant="underlined" color="primary"></v-text-field>
                <v-btn @click="setUpUser()" color="primary">Submit</v-btn>
            </v-container>
            <v-container v-else-if="!loginSuccessful">
                <v-text-field name="email" label="Email" v-model="email" variant="underlined" color="primary"></v-text-field>
                <v-text-field name="password" label="Password" v-model="password" type="password" variant="underlined" color="primary"></v-text-field>
                <v-btn @click="login()" color="primary">Login</v-btn>
            </v-container>
            <v-container v-else-if="needsOTP">
                <div v-if="settingUpMFA">Enter the OTP code to complete your MFA setup</div>
                <v-text-field name="OTP" label="Enter OTP" v-model="otp" variant="underlined" color="primary"></v-text-field>
                <v-btn @click="submitCode()" color="primary">Submit</v-btn>
            </v-container>
            <v-container v-else-if="settingUpMFA">
                Please set up your OTP by entering this code into Google Authenticator.<br />
                <span class="secretCode">{{ secretCode }}</span
                ><br />
                Once complete, click <span class="text-primary link" @click="goToOTP()">here</span> to enter your code
            </v-container>
        </div>
    </v-card>
</template>

<script lang="ts">
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import * as CONFIG from '../utils/config';
import { setAuthHeader } from '../services/http';

const config = {
    UserPoolId: CONFIG.paramCognitoPoolId,
    ClientId: CONFIG.paramCognitoClientId,
    Storage: new AmazonCognitoIdentity.CookieStorage(CONFIG.cookieStorageSettings)
};

let gCognitoUser;

export default {
    name: 'auth',
    data: () => ({
        isAuthed: false,
        email: '',
        password: '',
        signUpEmail: '',
        signUpPassword: '',
        code: '',
        secretCode: '',
        loginSuccessful: false,
        needsOTP: false,
        settingUpMFA: false,
        otp: '',
        settingUpUser: false,
        firstName: '',
        lastName: '',
        newPassword: '',
        cardTitle: 'Login',
        errorMessage: null
    }),
    methods: {
        setAuth: function (token: string) {
            sessionStorage.token = token;
            setAuthHeader(sessionStorage.token);
            this.isAuthed = true;
        },
        login: async function () {
            const authenticationData = {
                Username: this.email,
                Password: this.password
            };
            const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(authenticationData);

            const userPool = new AmazonCognitoIdentity.CognitoUserPool(config);

            const userData = {
                Username: this.email,
                Pool: userPool,
                Storage: new AmazonCognitoIdentity.CookieStorage(CONFIG.cookieStorageSettings)
            };

            const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

            const that = this;

            gCognitoUser = cognitoUser;

            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: function (result) {
                    const accessToken = result.getAccessToken().getJwtToken();
                    that.setAuth(accessToken);
                    that.$emit('auth');
                },
                onFailure: function () {
                    that.errorMessage = 'Invalid Username or Password';
                },
                newPasswordRequired: function () {
                    that.settingUpUser = true;
                    that.cardTitle = 'User Setup';
                },
                totpRequired: function () {
                    that.loginSuccessful = true;
                    that.needsOTP = true;
                    that.cardTitle = 'Enter OTP';
                },
                mfaSetup: function () {
                    gCognitoUser.associateSoftwareToken({
                        associateSecretCode: function (secretCode) {
                            that.loginSuccessful = true;
                            that.settingUpMFA = true;
                            that.settingUpUser = false;
                            that.secretCode = secretCode;
                            that.cardTitle = 'MFA Setup';
                        }
                    });
                }
            });
        },
        verifySoftwareToken: function () {
            const that = this;
            const challengeAnswer = this.otp;
            gCognitoUser.verifySoftwareToken(challengeAnswer, 'My TOTP device', {
                onSuccess: function (result) {
                    const accessToken = result.getAccessToken().getJwtToken();
                    that.setAuth(accessToken);
                    that.settingUpMFA = false;
                    that.needsOTP = false;
                    that.$emit('auth');
                },

                onFailure: function (err) {
                    that.errorMessage = err.message;
                }
            });
        },
        submitCode: function () {
            //when setting up MFA for the first time, we want to call verifySoftwareToken instead
            if (this.settingUpMFA) {
                this.verifySoftwareToken();
                return;
            }
            const that = this;
            gCognitoUser.sendMFACode(
                this.otp,
                {
                    onSuccess: function (result) {
                        const accessToken = result.getAccessToken().getJwtToken();
                        that.setAuth(accessToken);
                        that.$emit('auth');
                    },

                    onFailure: function (err) {
                        that.errorMessage = err.message;
                    }
                },
                'SOFTWARE_TOKEN_MFA'
            );
        },
        goToOTP: function () {
            this.needsOTP = true;
        },
        setUpUser: function () {
            //this assumes the user pool has been set up to require given name and family name only
            const attributes = {
                family_name: this.lastName,
                given_name: this.firstName
            };
            const that = this;
            gCognitoUser.completeNewPasswordChallenge(this.newPassword, attributes, {
                onSuccess: function (result) {
                    that.loginSuccessful = true;
                    that.$emit('auth');
                },
                onFailure: function (err) {
                    that.errorMessage = err.message;
                }
            });
        }
    },
    watch: {
        errorMessage: function (val) {
            if (val !== null) {
                this.$emit('error', this.errorMessage);
                this.errorMessage = null;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.secretCode {
    background-color: #efefef;
    font-family: 'Courier';
}
.link {
    text-decoration: underline;
    cursor: pointer;
}
</style>
